import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Send,
  Eye,
  EyeOff,
  Star,
  StarOff,
  History,
  Trash2,
  Settings,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Spinner, RichTextEditor, useToast } from '@centraoneplus/ui';
import { useKnowledgeStore } from '@stores/knowledgeStore';
import { knowledgeService, type ArticleVersion } from '@services/knowledge.service';
import { diffLines, htmlToText } from './articleDiff';
import { knowledgeAnalyticsService } from '@services/knowledge-analytics.service';
import { CARD, CARD_CLASS, CONTROL, DANGER, KbHeader, PRIMARY, WarnBand } from './shell';
import { SectionRule, StatusChip } from './ui';
import { daysSince, isStale } from './types';
import { sanitizeArticleHtml } from '@centraoneplus/ui';
import { ThemedSelect } from '@components/common/ThemedSelect';

/**
 * Strip anything executable from article HTML before it is injected via
 * dangerouslySetInnerHTML in the preview. Article content is author-supplied
 * and also renders in the public customer portal, so unsanitised HTML is a
 * stored-XSS vector (bug #9158): remove script/style/embeds, inline event
 * handlers (on*), and javascript: URLs while preserving normal formatting.
 * Mirrors the sanitizer used for ticket message bodies.
 */

// Phase 42 (artboards 18a-18f) — the editor joins the KB set.
//
// It was the last screen in the family still in the grey dialect: a white
// `.card`, a blue `.btn-primary` and gray-500 labels, reached from a register
// that is already floating Graphite cards. Nothing about what it DOES changes
// here — same state, same handlers, same calls, same labels — only what it is
// painted with.
//
// The field shapes are the kit's (34px, 8px radius, border moves to brass on
// focus and nothing else moves). They are spelled out here rather than
// imported as components because the sidebar's controls are native elements
// with ids that `htmlFor` already points at, and swapping them for the kit's
// button-based Checkbox/Input would change the DOM the form is wired to.
const FIELD =
  'h-[34px] rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] ' +
  'px-3 text-[13px] text-[var(--co-ink)] placeholder-[var(--co-ink-4)] outline-none ' +
  'transition-colors focus:border-[var(--co-brass)]';

const AREA =
  'w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-[10px] ' +
  'text-[12.5px] leading-[1.5] text-[var(--co-ink)] placeholder-[var(--co-ink-4)] outline-none ' +
  'transition-colors focus:border-[var(--co-brass)]';

const CHECK = 'h-[14px] w-[14px] flex-none rounded-[3px] border-[var(--co-line-strong)] accent-[var(--co-brass-ink)]';

/** A toggled header control reads as pressed: brass border, brass wash. */
const PRESSED: React.CSSProperties = {
  borderColor: 'var(--co-brass)',
  background: 'var(--co-brass-wash)',
  color: 'var(--co-brass-ink)',
};

/** Label above its control, mono, at the register's column-head weight. */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="co-mono mb-[6px] block text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-4)]">
        {label}
      </label>
      {children}
      {hint && <p className="mt-[5px] text-[11px] text-[var(--co-ink-4)]">{hint}</p>}
    </div>
  );
}

export default function ArticleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const toast = useToast();

  const {
    article,
    categories,
    isLoadingArticle,
    isSaving,
    error,
    fetchArticle,
    fetchCategories,
    createArticle,
    updateArticle,
    publishArticle,
    unpublishArticle,
    deleteArticle,
    resetArticle,
  } = useKnowledgeStore();

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState<ArticleVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isReembedding, setIsReembedding] = useState(false);
  const [expandedVersionId, setExpandedVersionId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Feedback state
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<'helpful' | 'not_helpful' | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  // Tracks which article id we've already counted a view for (prevents the
  // StrictMode double-invoke in dev from recording 2 views per open).
  const viewedArticleRef = useRef<string | null>(null);

  // Fetch article and categories on mount
  useEffect(() => {
    fetchCategories();
    if (!isNew && id) {
      fetchArticle(id);
      // Record a view once per opened article (best-effort, fire-and-forget).
      if (viewedArticleRef.current !== id) {
        viewedArticleRef.current = id;
        knowledgeService.recordView(id).catch(() => {
          /* view tracking is best-effort */
        });
      }
    }
    return () => resetArticle();
  }, [id, isNew]);

  // Populate form when article loads
  useEffect(() => {
    if (article && !isNew) {
      setTitle(article.title);
      setContent(article.content);
      setCategoryId(article.categoryId || '');
      setExcerpt(article.excerpt || '');
      setMetaTitle(article.metaTitle || '');
      setMetaDescription(article.metaDescription || '');
      setTags(article.tags || []);
      setIsPublic(article.isPublic);
      setIsFeatured(article.isFeatured);
      setHasChanges(false);
    }
  }, [article, isNew]);

  // Track changes
  useEffect(() => {
    if (!isNew && article) {
      const changed =
        title !== article.title ||
        content !== article.content ||
        categoryId !== (article.categoryId || '') ||
        excerpt !== (article.excerpt || '') ||
        metaTitle !== (article.metaTitle || '') ||
        metaDescription !== (article.metaDescription || '') ||
        JSON.stringify(tags) !== JSON.stringify(article.tags) ||
        isPublic !== article.isPublic ||
        isFeatured !== article.isFeatured;
      setHasChanges(changed);
    } else if (isNew) {
      setHasChanges(title !== '' || content !== '');
    }
  }, [title, content, categoryId, excerpt, metaTitle, metaDescription, tags, isPublic, isFeatured, article, isNew]);

  const handleSave = async (options?: { silent?: boolean }) => {
    try {
      const dto = {
        title,
        content,
        categoryId: categoryId || undefined,
        excerpt: excerpt || undefined,
        metaTitle: metaTitle || undefined,
        metaDescription: metaDescription || undefined,
        tags,
        isPublic,
        isFeatured,
      };

      if (isNew) {
        await createArticle(dto);
        setHasChanges(false);
        if (!options?.silent) {
          toast.success('Saved', 'Article created');
        }
        // Close the creation editor and return to the list so the user sees
        // the newly-created article (bug #9307: editor stayed open after save).
        navigate('/knowledge', { replace: true });
        return;
      } else if (id) {
        await updateArticle(id, dto);
      }
      setHasChanges(false);
      if (!options?.silent) {
        toast.success('Saved', 'Article saved');
      }
    } catch (err) {
      // Surface the backend's validation message, not the raw axios
      // "Request failed with status code 409" string (bug #9620). A duplicate
      // title generates a duplicate slug -> the API returns 409; show a clear,
      // user-friendly message instead of the technical "slug already exists".
      const e = err as {
        response?: { status?: number; data?: { message?: string; error?: string } };
        message?: string;
      };
      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.message || e?.response?.data?.error;
      const isDuplicate =
        status === 409 || /already exists|duplicate|slug/i.test(String(apiMsg ?? ''));
      if (isDuplicate) {
        toast.error(
          'Duplicate article',
          'An article with this title already exists. Please use a unique title.'
        );
      } else {
        toast.error('Save failed', apiMsg || e?.message || 'Failed to save the article');
      }
      console.error('Failed to save:', err);
    }
  };

  const handlePublish = async () => {
    if (!id || isNew) return;
    try {
      // Save first if there are changes
      if (hasChanges) {
        await handleSave({ silent: true });
      }
      await publishArticle(id);
      toast.success('Article published');
    } catch (err) {
      console.error('Failed to publish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Publish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to publish article');
    }
  };

  /**
   * Refresh the assistant's copy of this article.
   *
   * PUBLISHING already queues embedding server-side (`articles.service.ts`
   * calls `queueArticleProcessing` with `generateEmbeddings`), which is why
   * the publish button can honestly say "& re-embed" without a second call
   * from here — adding one would double-queue the same article.
   *
   * SAVING an already-published article does not. `update()` re-indexes
   * Elasticsearch and stops, so keyword search finds the new text while the
   * assistant keeps quoting the old. That gap is why this button exists and
   * why the band above it says so out loud rather than leaving the author to
   * discover it from a customer.
   */
  const handleReembed = async () => {
    if (!id || isNew) return;
    setIsReembedding(true);
    try {
      await knowledgeAnalyticsService.reindex([id]);
      toast.success('Queued', 'The assistant will re-read this article shortly.');
    } catch (err) {
      console.error('Failed to queue re-embedding:', err);
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      toast.error(
        'Could not queue re-embedding',
        e?.response?.data?.message || e?.message || 'The assistant is still using the previous text.',
      );
    } finally {
      setIsReembedding(false);
    }
  };

  const handleUnpublish = async () => {
    if (!id || isNew) return;
    try {
      await unpublishArticle(id);
      toast.success('Article unpublished');
    } catch (err) {
      console.error('Failed to unpublish:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Unpublish failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to unpublish article');
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    if (!confirm('Are you sure you want to delete this article? This action cannot be undone.')) return;
    try {
      await deleteArticle(id);
      toast.success('Article deleted');
      navigate('/knowledge');
    } catch (err) {
      console.error('Failed to delete:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Delete failed', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to delete article');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const loadVersions = async () => {
    if (!id || isNew) return;
    setIsLoadingVersions(true);
    try {
      const versionHistory = await knowledgeService.getArticleVersions(id);
      setVersions(versionHistory);
    } catch (err) {
      console.error('Failed to load versions:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Failed to load version history', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Could not load version history');
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleShowVersions = () => {
    setShowVersions(!showVersions);
    if (!showVersions) {
      loadVersions();
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFeedback = async (isHelpful: boolean) => {
    if (!id || isNew || isSubmittingFeedback) return;

    setIsSubmittingFeedback(true);
    try {
      await knowledgeService.submitFeedback(id, isHelpful);
      setFeedbackSubmitted(isHelpful ? 'helpful' : 'not_helpful');
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      const e = err as { response?: { data?: { message?: string; error?: { message?: string } } }; message?: string };
      toast.error('Failed to submit feedback', e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Could not submit your feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  if (isLoadingArticle && !isNew) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    // The page owns its scroll so the header card can stay put while a long
    // article moves under it — the sticky bar the grey version had, in the
    // shape the rest of the product uses (see TicketFormPage).
    <div className="scrollbar-hide h-full overflow-y-auto">
      <div className="sticky top-0 z-10 bg-[var(--co-canvas)] pr-4 pb-[14px] pt-4">
        <KbHeader
          title={isNew ? 'New Article' : 'Edit Article'}
          lead={
            <button
              type="button"
              onClick={() => navigate('/knowledge')}
              aria-label="Back to the knowledge base"
              className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          }
          meta={
            /* The same two facts the grey header carried — the status, and
               whether the buffer is dirty. Nothing new is introduced here. */
            !isNew && article ? (
              <>
                <StatusChip status={article.status} />
                {hasChanges && (
                  <span
                    className="inline-flex items-center border px-[6px] py-[3px] text-[9.5px] font-semibold tracking-[.12em]"
                    style={{ borderColor: 'var(--co-terracotta)', color: 'var(--co-terracotta)' }}
                  >
                    UNSAVED CHANGES
                  </span>
                )}
              </>
            ) : null
          }
          actions={
            <>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                aria-pressed={showPreview}
                className={CONTROL}
                style={showPreview ? PRESSED : undefined}
              >
                {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                Preview
              </button>

              {!isNew && (
                <button
                  type="button"
                  onClick={handleShowVersions}
                  aria-pressed={showVersions}
                  className={CONTROL}
                  style={showVersions ? PRESSED : undefined}
                >
                  <History className="h-4 w-4" />
                  History
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                aria-pressed={showSettings}
                className={CONTROL}
                style={showSettings ? PRESSED : undefined}
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>

              <button
                type="button"
                onClick={() => handleSave()}
                disabled={isSaving || !title.trim()}
                className={PRIMARY}
              >
                {isSaving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />}
                Save
              </button>

              {!isNew && article?.status === 'draft' && (
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={isSaving || !title.trim()}
                  className={PRIMARY}
                >
                  <Send className="h-4 w-4" />
                  {/* Publishing changes what customers read AND what the
                      assistant says; a button labelled just "Publish" hides
                      the second. The server already queues the embedding, so
                      this is a truer label, not an extra call. */}
                  Publish &amp; re-embed
                </button>
              )}

              {!isNew && article?.status === 'published' && (
                <button
                  type="button"
                  onClick={handleUnpublish}
                  disabled={isSaving}
                  className={CONTROL}
                >
                  Unpublish
                </button>
              )}
            </>
          }
        />
      </div>

      <div className="space-y-[14px] pr-4 pb-4">
        {/* Error Message — the same string, in the set's danger tone rather
            than a red card in a dialect of its own. */}
        {error && <WarnBand tone="burgundy" headline="Last action failed" body={error} />}

        {/* 18b. Two things about this article that the editor alone cannot
            show: how long it has gone unreviewed, and whether the assistant is
            still serving the previous text. */}
        {!isNew && article && (
          <>
            {isStale(article.updatedAt) && (
              <WarnBand
                tone="terracotta"
                headline={`Last reviewed ${daysSince(article.updatedAt)} days ago`}
                body={`Read ${(article.viewCount ?? 0).toLocaleString()} ${
                  (article.viewCount ?? 0) === 1 ? 'time' : 'times'
                } since it was last touched. Nothing here is wrong yet — but nobody has checked.`}
              />
            )}
            {article.status === 'published' && (
              <WarnBand
                tone="machine"
                headline="Saving updates search, not the assistant"
                body="Editing a published article re-indexes keyword search straight away, but the assistant keeps answering from the text it embedded last. Re-embed when the meaning has changed — not for a typo."
                action={{
                  label: isReembedding ? 'Queueing…' : 'Re-embed now',
                  onClick: handleReembed,
                }}
              />
            )}
          </>
        )}

        {/* Main Content */}
        <div className="flex flex-wrap items-start gap-[14px]">
          {/* Editor */}
          <div className={`min-w-0 flex-1 ${showSettings || showVersions ? 'max-w-3xl' : ''}`}>
            <div className={`${CARD_CLASS} space-y-5 p-5`} style={CARD}>
              {/* Title */}
              <div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Article title..."
                  className="w-full border-0 bg-transparent p-0 text-[26px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)] outline-none placeholder-[var(--co-ink-4)] focus:outline-none focus:ring-0"
                />
                <div className="mt-4 h-px bg-[var(--co-line-subtle)]" />
              </div>

              {/* Content Editor / Preview */}
              {showPreview ? (
                <div className="min-h-[400px]">
                  <div className="prose dark:prose-invert max-w-none">
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(content) }}
                      className="text-[var(--co-ink)]"
                    />
                  </div>

                  {/* Feedback Section - only show for existing published articles */}
                  {!isNew && article?.status === 'published' && (
                    <div className="mt-8 border-t border-[var(--co-line-subtle)] pt-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-4)]">
                          Was this article helpful?
                        </span>
                        {feedbackSubmitted ? (
                          <span
                            className="flex items-center gap-[6px] text-[12.5px]"
                            style={{ color: 'var(--co-green-ink)' }}
                          >
                            {feedbackSubmitted === 'helpful' ? (
                              <ThumbsUp className="h-4 w-4" />
                            ) : (
                              <ThumbsDown className="h-4 w-4" />
                            )}
                            Thank you for your feedback!
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleFeedback(true)}
                              disabled={isSubmittingFeedback}
                              className={CONTROL}
                            >
                              <ThumbsUp className="h-4 w-4" />
                              Yes
                            </button>
                            <button
                              type="button"
                              onClick={() => handleFeedback(false)}
                              disabled={isSubmittingFeedback}
                              className={CONTROL}
                            >
                              <ThumbsDown className="h-4 w-4" />
                              No
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <RichTextEditor
                    value={content}
                    onChange={(html) => setContent(html)}
                    placeholder="Write your article content..."
                    minHeight={400}
                    className="border-[var(--co-line)]"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Settings */}
          {showSettings && (
            <div className="w-full flex-none space-y-[14px] lg:w-[320px]">
              <div className={`${CARD_CLASS} space-y-4 p-5`} style={CARD}>
                <SectionRule label="SETTINGS" />

                {/* Category */}
                <Field label="Category">
                  <ThemedSelect
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className={`${FIELD} w-full`}
                  >
                    <option value="">No category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </ThemedSelect>
                </Field>

                {/* Excerpt */}
                <Field label="Excerpt">
                  <textarea
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    placeholder="Short description..."
                    className={AREA}
                    rows={2}
                  />
                </Field>

                {/* Tags */}
                <Field label="Tags">
                  {tags.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-[6px]">
                      {tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-[6px] rounded-md border border-[var(--co-line)] bg-[var(--co-selected)] px-2 py-[3px] text-[12px] text-[var(--co-ink-2)]"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            aria-label={`Remove ${tag}`}
                            className="text-[var(--co-ink-4)] hover:text-[var(--co-burgundy)]"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add tag..."
                      className={`${FIELD} min-w-0 flex-1`}
                    />
                    <button type="button" onClick={handleAddTag} className={CONTROL}>
                      Add
                    </button>
                  </div>
                </Field>

                {/* Visibility */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className={CHECK}
                    />
                    <label htmlFor="isPublic" className="text-[12.5px] text-[var(--co-ink-2)]">
                      Visible to public
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isFeatured"
                      checked={isFeatured}
                      onChange={(e) => setIsFeatured(e.target.checked)}
                      className={CHECK}
                    />
                    <label
                      htmlFor="isFeatured"
                      className="flex items-center gap-[6px] text-[12.5px] text-[var(--co-ink-2)]"
                    >
                      {isFeatured ? (
                        <Star
                          className="h-4 w-4"
                          style={{ color: 'var(--co-terracotta)', fill: 'var(--co-terracotta)' }}
                        />
                      ) : (
                        <StarOff className="h-4 w-4 text-[var(--co-ink-4)]" />
                      )}
                      Featured article
                    </label>
                  </div>
                </div>
              </div>

              {/* SEO Settings */}
              <div className={`${CARD_CLASS} space-y-4 p-5`} style={CARD}>
                <SectionRule label="SEO" />

                <Field label="Meta Title">
                  <input
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder={title || 'Page title for search engines'}
                    className={`${FIELD} w-full`}
                  />
                </Field>

                <Field
                  label="Meta Description"
                  hint={`${metaDescription.length}/160 characters recommended`}
                >
                  <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Description for search engines..."
                    className={AREA}
                    rows={3}
                  />
                </Field>
              </div>

              {/* Danger Zone */}
              {!isNew && (
                <div className={`${CARD_CLASS} space-y-4 p-5`} style={CARD}>
                  <SectionRule label="DANGER ZONE" />
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSaving}
                    className={`${DANGER} w-full`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Article
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Sidebar - Version History */}
          {showVersions && (
            <div className="w-full flex-none lg:w-[320px]">
              <div className={`${CARD_CLASS} space-y-4 p-5`} style={CARD}>
                <SectionRule label="VERSION HISTORY" />

                {isLoadingVersions ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : versions.length === 0 ? (
                  <p className="py-8 text-center text-[12.5px] text-[var(--co-ink-4)]">
                    No version history yet
                  </p>
                ) : (
                  <div className="space-y-[10px]">
                    {versions.map((version, index) => {
                      const newerState =
                        index === 0
                          ? { title: article?.title ?? title, content: article?.content ?? content }
                          : { title: versions[index - 1].title, content: versions[index - 1].content };
                      const isExpanded = expandedVersionId === version.id;
                      const changes = isExpanded
                        ? diffLines(htmlToText(version.content), htmlToText(newerState.content)).filter(
                            (line) => line.type !== 'unchanged',
                          )
                        : [];
                      const titleChanged = isExpanded && newerState.title !== version.title;
                      return (
                      <div
                        key={version.id}
                        onClick={() => setExpandedVersionId(isExpanded ? null : version.id)}
                        className="cursor-pointer rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-3 hover:border-[var(--co-brass)]"
                        style={isExpanded ? { background: 'var(--co-selected)' } : undefined}
                      >
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="co-mono text-[10.5px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink)]">
                            Version {version.version}
                          </span>
                          <span className="co-mono text-[10px] text-[var(--co-ink-4)]">
                            {formatDate(version.createdAt)}
                          </span>
                        </div>
                        <p className="truncate text-[12.5px] text-[var(--co-ink-2)]">
                          {version.title}
                        </p>
                        {version.authorName && (
                          <p className="mt-1 text-[11px] text-[var(--co-ink-4)]">by {version.authorName}</p>
                        )}
                        {version.changeNote && (
                          <p className="mt-1 text-[11px] italic text-[var(--co-ink-4)]">{version.changeNote}</p>
                        )}
                        <div className="co-mono mt-2 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[.12em] text-[var(--co-brass-ink)]">
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {isExpanded ? 'Hide changes' : 'View changes'}
                        </div>
                        {isExpanded && (
                          <div
                            className="mt-2 border-t border-[var(--co-line-subtle)] pt-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <p className="mb-2 text-[11px] text-[var(--co-ink-4)]">
                              {index === 0
                                ? `Changes since Version ${version.version} (current article)`
                                : `Changes: Version ${version.version} → Version ${versions[index - 1].version}`}
                            </p>
                            {titleChanged && (
                              <div className="mb-2 text-[11px]">
                                <span className="text-[var(--co-ink-4)]">Title: </span>
                                <span className="line-through" style={{ color: 'var(--co-burgundy)' }}>
                                  {version.title}
                                </span>
                                <span className="text-[var(--co-ink-4)]"> → </span>
                                <span style={{ color: 'var(--co-green-ink)' }}>{newerState.title}</span>
                              </div>
                            )}
                            {changes.length === 0 && !titleChanged ? (
                              <p className="text-[11px] text-[var(--co-ink-4)]">
                                No content changes in this revision.
                              </p>
                            ) : (
                              <div className="co-mono max-h-64 space-y-0.5 overflow-y-auto text-[11px] leading-snug">
                                {changes.map((line, li) => (
                                  <div
                                    key={li}
                                    className="whitespace-pre-wrap break-words rounded px-1"
                                    style={
                                      line.type === 'added'
                                        ? {
                                            color: 'var(--co-green-ink)',
                                            background: 'var(--co-green-wash)',
                                          }
                                        : {
                                            color: 'var(--co-burgundy)',
                                            background: 'var(--co-burgundy-wash)',
                                          }
                                    }
                                  >
                                    <span className="mr-1 select-none">{line.type === 'added' ? '+' : '−'}</span>
                                    {line.text || ' '}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
